import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'

import { PaddingInset, spacing, Stack, Text } from '@cash-design-system/react'
import {
  FormBlockerElementOptionPickerElement,
  FormBlockerElementOptionPickerElementOption,
  FormBlockerElementOptionPickerElementOptionTextColor,
} from '@cash-web/protos/squareup/franklin/form_blocker.pb'
import logger from '@cash-web/shared/util-logger'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-arcade-migration'
import { RadioGroup } from '@cash-web/shared-feature-form'
import { useColorProtoAdapter } from '@cash-web/shared-proto-adapters-ui-color'

type ValidOptionWithValue = Omit<FormBlockerElementOptionPickerElementOption, 'title' | 'id'> & {
  value: string
  id: string
}

type ValidOptionWithSubtitle = Omit<FormBlockerElementOptionPickerElementOption, 'subtitle' | 'id'> & {
  subtitle: string
  id: string
}

type ValidOption = ValidOptionWithValue | ValidOptionWithSubtitle

function isValidOption(option: FormBlockerElementOptionPickerElementOption): option is ValidOption {
  return Boolean((option.value || option.subtitle) && option.id)
}
const Eyebrow = styled.div<{ eyebrowColor?: string }>`
  color: ${props => props.eyebrowColor ?? props.theme.colors.eyebrowHeader};
  text-transform: uppercase;
  ${props => props.theme.typography.identifier};
`

const Subtitle = styled.div<{ subtitleColor?: FormBlockerElementOptionPickerElementOptionTextColor }>`
  ${({ subtitleColor, theme }) => (subtitleColor === 'WARNING' ? `color: ${theme.colors.error};` : '')};
`

function logUnimplemented(property: string) {
  logger.warn(new Error(`OptionPickerElement: '${property}' not implemented in option`))
}

export const OptionPickerElement = ({
  formSectionIndex,
  options,
  selected_option,
  is_optional,
}: FormBlockerElementOptionPickerElement & { formSectionIndex: number }) => {
  const { t } = useTranslation()
  const { getColorValue } = useColorProtoAdapter()
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')

  const validOptions = options?.filter(isValidOption)

  const selectedOptionId = options && selected_option !== undefined ? options[selected_option].id : undefined

  if (!validOptions?.length) {
    logger.error(new Error('OptionPickerElement: Not rendered because there were no valid options'))
    return null
  }

  const contents = (
    <RadioGroup
      name={`${formSectionIndex}.option_picker_result.option_id`}
      inputStyle={isArcadeMigrationEnabled ? 'input-card' : 'inline'}
      defaultValue={selectedOptionId}
      rules={{
        required: {
          value: !is_optional,
          message: t('Select an option', {
            context: 'error text when cash out speed has not been chosen in form',
          }),
        },
      }}
      options={validOptions.map(option => {
        if (option.unselectable) logUnimplemented('unselectable')
        if (option.eyebrow_header_color) logUnimplemented('eyebrow_header_color')
        if (option.eyebrow_header_text) logUnimplemented('eyebrow_header')

        const eyebrowColor = getColorValue(option.eyebrow_header_color)

        if (isArcadeMigrationEnabled) {
          return {
            label: option.value ?? '',
            cellProps: {
              body: (
                <Stack space={spacing.xxs}>
                  {option.subtitle}
                  {option.eyebrow_header_text && (
                    <Eyebrow eyebrowColor={eyebrowColor}>{option.eyebrow_header_text}</Eyebrow>
                  )}
                </Stack>
              ),
            },
            value: option.id,
          }
        }
        return {
          label: (
            <Stack space={spacing.xxs}>
              {option.eyebrow_header_text && (
                <Eyebrow eyebrowColor={eyebrowColor}>{option.eyebrow_header_text}</Eyebrow>
              )}
              {option.value && <Text typography="labelMedium">{option.value}</Text>}
              {option.subtitle && <Subtitle subtitleColor={option.subtitle_color}>{option.subtitle}</Subtitle>}
            </Stack>
          ),
          value: option.id,
        }
      })}
    />
  )

  if (isArcadeMigrationEnabled) return contents
  return <PaddingInset data-testid="padding-inset">{contents}</PaddingInset>
}
